import React, { useEffect, useMemo, useState } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import EditMenuButton from '../../Modal/EditMenuButton';
import ConfirmDeleteDialog from '../../Modal/ConfirmDelete';
import { Box, Button, FormControl } from '@mui/material';
import ModifyPriorityModal from './ModifyPriorityModal';
import PriorityApiService from '../../../api/PriorityApiService';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../Common/SnackbarProvider';

const Priorities = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();

    const [priorities, setPriorities] = useState([]);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedPriority, setSelectedPriority] = useState();

    const columns = useMemo(() => [
        {
            field: 'prId',
            headerName: t('common.id'),
            flex: 0.5,
        },
        {
            field: 'prName',
            headerName: t('prioritiesPage.column.name'),
            flex: 2,
        },
        {
            field: 'prSortSeq',
            headerName: t('fieldName.sortSequence'),
            flex: 2,
        },
        {
            field: 'prBackgroundColor',
            headerName: t('prioritiesPage.column.backgroundColor'),
            renderCell: (params) => {
                return(<label style={{ backgroundColor: params.row.prBackgroundColor }}>{params.row.prBackgroundColor}</label>)
            },
            flex: 2,
        },
        {
            field: 'edit',
            headerName: t('common.edit'),
            flex: 0.5,
            filterable: false,
            sortable: false,
            renderCell: (params) => {
                return <EditMenuButton
                    params={params}
                    setShowDeleteDialog={setShowDeleteDialog}
                    setCurrentSelectedRecord={setSelectedPriority}
                    onEditFunc={() => {
                        setShowEditDialog(true);
                        setSelectedPriority(params.row)
                    }}
                />;
            },
        }
    ], [t]);

    const priorityApiService = new PriorityApiService();

    async function getPriorities () {
        const priorities = await priorityApiService.GetPriorities();
        setPriorities(priorities.data);
    }

    async function onDeleteClick(value) {
        if (!value) {
            setShowDeleteDialog(false);
            return;
        }

        if (selectedPriority) {
            const deletedPriority = await priorityApiService.deletePriority(selectedPriority.prId);
            if (deletedPriority && deletedPriority.success) {
                snackbar.warning(t('prioritiesPage.toast.deleted'));
                await getPriorities();
            }
        }

        setShowDeleteDialog(false);
    }

    function handleEditClose() {
        setShowEditDialog(false);
        setSelectedPriority(null);
    }

    useEffect(() => {
        getPriorities();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showEditDialog])

    return(
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--ct-header-h, 56px))' }}>
            <Box sx={{ mb: 2 }}>
                <h2>{t('prioritiesPage.title')}</h2>
            </Box>
            <Box sx={{ mb: 2 }}>
                <div style={{ display: 'inline-block', justifyContent: 'space-between' }}>
                    <FormControl sx={{ m: 1, minWidth: 120, float: 'left' }}>
                        <Button size={'large'} variant="contained" onClick={() => setShowEditDialog(true) }>{t('prioritiesPage.createButton')}</Button>
                    </FormControl>
                </div>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                <DataGridPro
                    columns={columns}
                    rows={priorities}
                    getRowId={(row) => row.prId}
                    getRowHeight={() => 'auto'}
                />
            </Box>
            <ConfirmDeleteDialog
                onClose={onDeleteClick}
                open={showDeleteDialog}
            />
            <ModifyPriorityModal
                open={showEditDialog}
                onClose={handleEditClose}
                selectedPriority={selectedPriority}
                priorityApiService={priorityApiService}
                onCancel={handleEditClose}
            />
        </Box>
    );
}

export default Priorities;
